import { useCallback, useEffect, useState } from "react";
import { Detective, Plus } from "@phosphor-icons/react";
import { BloomButton } from "@/components/styleguide/ui/BloomButton";
import { BloomBadge } from "@/components/styleguide/ui/BloomBadge";
import { cn } from "@/lib/utils";
import { api } from "../api";
import { WordEditor } from "./WordEditor";

const TABS = [
  { key: "open", label: "Open" },
  { key: "added", label: "Added" },
  { key: "dismissed", label: "Dismissed" },
];

export default function AdminMissing() {
  const [tab, setTab] = useState("open");
  const [items, setItems] = useState(null);
  const [editor, setEditor] = useState(null); // preset word string

  const load = useCallback(async () => {
    setItems(await api(`/admin/missing-words?status=${tab}`));
  }, [tab]);

  useEffect(() => { setItems(null); load().catch(() => setItems([])); }, [load]);

  const dismiss = async (m) => {
    await api(`/admin/missing-words/${m.missing_id}`, { method: "PATCH", body: { status: "dismissed" } });
    load();
  };

  return (
    <div className="mx-auto max-w-4xl" data-testid="admin-missing">
      <h1 className="font-display text-3xl font-black tracking-tight text-foreground">Missing Words</h1>
      <p className="mt-1 text-sm font-semibold text-muted-foreground">Words children tapped that Bloom doesn't know yet — your content roadmap, straight from real learning.</p>

      <div className="mt-5 flex gap-1.5">
        {TABS.map(({ key, label }) => (
          <button key={key} data-testid={`missing-tab-${key}`} onClick={() => setTab(key)}
            className={cn("rounded-full border-2 px-4 py-2 text-sm font-extrabold transition", tab === key ? "border-primary bg-accent text-primary-strong" : "border-border text-muted-foreground hover:border-primary/40")}>
            {label}
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-3">
        {items === null && <div className="h-40 animate-pulse rounded-3xl bg-muted" />}
        {items?.map((m) => (
          <div key={m.missing_id} className="rounded-3xl border border-border bg-card p-5 shadow-soft" data-testid={`missing-card-${m.normalized}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue/12"><Detective size={18} weight="bold" className="text-blue-ink" /></span>
                <div>
                  <p className="font-display text-lg font-black capitalize text-foreground">{m.word_display}</p>
                  <p className="text-xs font-semibold text-muted-foreground">Last requested {new Date(m.last_requested_at).toLocaleString()}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <BloomBadge variant="primary" className="px-2.5 py-1 text-xs">{m.total_requests} requests</BloomBadge>
                <BloomBadge variant="teal" className="px-2.5 py-1 text-xs">{m.unique_children} {m.unique_children === 1 ? "child" : "children"}</BloomBadge>
              </div>
            </div>

            {m.sources?.length > 0 && (
              <p className="mt-3 text-sm font-semibold text-muted-foreground">
                Encountered from:{" "}
                {m.sources.map((s, i) => (
                  <span key={i}>
                    <b className="capitalize text-foreground">{s.source_word || "unknown"}</b> ({s.source_type} ×{s.count}){i < m.sources.length - 1 ? ", " : ""}
                  </span>
                ))}
              </p>
            )}

            {tab === "open" && (
              <div className="mt-4 flex gap-2">
                <BloomButton size="sm" leftIcon={Plus} onClick={() => setEditor(m.word_display)} data-testid={`add-to-library-${m.normalized}`}>
                  Add to library
                </BloomButton>
                <BloomButton size="sm" variant="ghost" onClick={() => dismiss(m)} data-testid={`dismiss-${m.normalized}`}>
                  Dismiss
                </BloomButton>
              </div>
            )}
            {tab === "added" && m.resolved_word_id && (
              <BloomBadge variant="success" className="mt-3 px-2.5 py-1 text-xs">Resolved — now in the Master Library</BloomBadge>
            )}
          </div>
        ))}
        {items?.length === 0 && (
          <div className="flex flex-col items-center gap-2 rounded-3xl border border-dashed border-border p-10 text-center">
            <Detective size={32} weight="bold" className="text-muted-foreground" />
            <p className="text-sm font-semibold text-muted-foreground">Nothing here — no vocabulary gaps {tab === "open" ? "right now" : `marked ${tab}`}.</p>
          </div>
        )}
      </div>

      {editor && <WordEditor presetWord={editor} onClose={() => setEditor(null)} onSaved={load} />}
    </div>
  );
}
